import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar, Clock, FileText, CheckCircle, XCircle, AlertCircle, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';

const AppointmentHistory = ({ member, appointments, onUpdate }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all'); // all, upcoming, completed, cancelled
  const [loading, setLoading] = useState(false);

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { color: 'bg-blue-100 text-blue-800', icon: Clock, labelKey: 'appointments.scheduled' },
      confirmed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, labelKey: 'appointments.confirmed' },
      completed: { color: 'bg-gray-100 text-gray-800', icon: CheckCircle, labelKey: 'appointments.completed' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle, labelKey: 'appointments.cancelled' },
      no_show: { color: 'bg-orange-100 text-orange-800', icon: AlertCircle, label: 'No Show' }
    };

    const config = statusConfig[status] || statusConfig.scheduled;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.labelKey ? t(config.labelKey) : config.label}
      </Badge>
    );
  };

  const getAppointmentTypeIcon = (type) => {
    const icons = {
      'check-up': '🩺',
      'consultation': '💬',
      'follow-up': '🔄',
      'urgent': '🚨',
      'treatment': '💉',
      'test': '🔬'
    };
    return icons[type] || '📅';
  };

  const filterAppointments = () => {
    if (!appointments) return [];
    
    const now = new Date();
    
    return appointments.filter(apt => {
      const aptDate = new Date(apt.appointment_date);
      
      if (filter === 'upcoming') {
        return aptDate > now && (apt.status === 'scheduled' || apt.status === 'confirmed');
      } else if (filter === 'completed') {
        return apt.status === 'completed';
      } else if (filter === 'cancelled') {
        return apt.status === 'cancelled' || apt.status === 'no_show';
      }
      return true; // all
    }).sort((a, b) => new Date(b.appointment_date) - new Date(a.appointment_date));
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!confirm(t('common.confirm') + '?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/appointments/${appointmentId}/cancel`, {
        reason: 'Cancelled by patient'
      });
      
      toast.success(t('appointments.cancelled'));
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const filteredAppointments = filterAppointments();
  const upcomingCount = appointments?.filter(apt => 
    new Date(apt.appointment_date) > new Date() && 
    (apt.status === 'scheduled' || apt.status === 'confirmed')
  ).length || 0;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('all')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{appointments?.length || 0}</p>
              </div>
              <Calendar className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('upcoming')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('tabs.upcoming')}</p>
                <p className="text-2xl font-bold text-blue-600">{upcomingCount}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('completed')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('appointments.completed')}</p>
                <p className="text-2xl font-bold text-green-600">
                  {appointments?.filter(a => a.status === 'completed').length || 0}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('cancelled')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('appointments.cancelled')}</p>
                <p className="text-2xl font-bold text-red-600">
                  {appointments?.filter(a => a.status === 'cancelled' || a.status === 'no_show').length || 0}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Appointments List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('tabs.usageHistory')}</CardTitle>
              <CardDescription>
                {filter === 'all' ? t('payments.allTransactions') : 
                 filter === 'upcoming' ? t('appointments.upcomingAppointments') :
                 filter === 'completed' ? t('appointments.completed') :
                 t('appointments.cancelled')}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                Total
              </Button>
              <Button
                variant={filter === 'upcoming' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('upcoming')}
              >
                {t('tabs.upcoming')}
              </Button>
              <Button
                variant={filter === 'completed' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('completed')}
              >
                {t('appointments.completed')}
              </Button>
              <Button
                variant={filter === 'cancelled' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('cancelled')}
              >
                {t('appointments.cancelled')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredAppointments.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{t('appointments.noUpcomingAppointments')}</p>
              <p className="text-sm text-gray-400 mt-1">
                {filter === 'upcoming' ? t('appointments.noUpcomingAppointments') :
                 filter === 'completed' ? t('appointments.noAppointmentHistory') :
                 filter === 'cancelled' ? t('appointments.cancelled') :
                 t('appointments.noAppointmentHistory')}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('payments.date')}</TableHead>
                    <TableHead>{t('appointments.appointmentType')}</TableHead>
                    <TableHead>{t('payments.status')}</TableHead>
                    <TableHead>{t('appointments.additionalNotes').split(' (')[0]}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAppointments.map((appointment) => {
                    const aptDate = new Date(appointment.appointment_date);
                    const isUpcoming = aptDate > new Date() && (appointment.status === 'scheduled' || appointment.status === 'confirmed');
                    
                    return (
                      <TableRow key={appointment.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <div>
                              <p className="font-medium">
                                {format(aptDate, 'MMM dd, yyyy')}
                              </p>
                              <p className="text-sm text-gray-500">
                                {format(aptDate, 'h:mm a')}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getAppointmentTypeIcon(appointment.appointment_type)}</span>
                            <span className="capitalize">{appointment.appointment_type.replace('-', ' ')}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(appointment.status)}
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs truncate text-sm text-gray-600">
                            {appointment.notes || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {isUpcoming && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancelAppointment(appointment.id)}
                              disabled={loading}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              {t('common.cancel')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AppointmentHistory;
